import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, getDay } from 'date-fns';
import { FiCheck, FiClock, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

// 요일 매핑 (JS getDay: 0=일, 1=월, ... 6=토)
const DAY_MAP = ['일', '월', '화', '수', '목', '금', '토'];

export default function Attendance() {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(false);
  const [pointBalance, setPointBalance] = useState(0);

  // 알림 설정 (tenant에서 가져옴)
  const notificationSettings = tenant?.attendanceNotification || {
    push: { enabled: true, checkIn: true, checkOut: true },
    alimtalk: { enabled: false, checkIn: true, checkOut: true }
  };

  // 선택된 날짜의 요일
  const selectedDay = DAY_MAP[getDay(new Date(selectedDate))];

  // 선택된 반이 오늘 수업이 있는지 확인
  const selectedClassData = classes.find(c => c._id === selectedClass);
  const hasScheduleToday = selectedClassData?.schedule?.some(s => s.day === selectedDay) || false;
  const isToday = selectedDate === format(new Date(), 'yyyy-MM-dd');

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date: selectedDate };
      if (selectedClass) {
        params.classGroupId = selectedClass;
      }
      const { data } = await api.get('/api/attendance/by-date', { params });

      // Convert to map for easy lookup
      const attendanceMap = {};
      (data.attendance || []).forEach(a => {
        const studentId = a.studentId?._id || a.studentId;
        attendanceMap[studentId] = a;
      });
      setAttendance(attendanceMap);
    } catch (error) {
      console.error('Failed to load attendance:', error);
      setAttendance({});
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedClass]);

  const loadClassStudents = useCallback(async () => {
    if (!selectedClass) {
      // 반 미선택시 모든 활성 학생 표시
      try {
        const { data } = await api.get('/api/users/students');
        setStudents(data.students || []);
      } catch (error) {
        setStudents([]);
      }
      return;
    }

    try {
      const { data } = await api.get(`/api/class-groups/${selectedClass}`);
      if (data.classGroup?.students && data.classGroup.students.length > 0) {
        // 해당 반의 학생만 표시
        setStudents(data.classGroup.students);
      } else {
        // 반에 학생이 없으면 빈 배열
        setStudents([]);
      }
    } catch (error) {
      console.error('Failed to load students:', error);
      setStudents([]);
    }
  }, [selectedClass]);

  useEffect(() => {
    loadClasses();
  }, []);

  useEffect(() => {
    loadClassStudents();
    loadAttendance();
  }, [loadClassStudents, loadAttendance]);

  const loadClasses = async () => {
    try {
      const [classesRes, pointsRes] = await Promise.all([
        api.get('/api/class-groups', { params: { active: true } }),
        api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }))
      ]);
      setClasses(classesRes.data.classGroups || []);
      setPointBalance(pointsRes.data.balance || 0);
      if (classesRes.data.classGroups?.length > 0) {
        setSelectedClass(classesRes.data.classGroups[0]._id);
      }
    } catch (error) {
      console.error('Failed to load classes:', error);
    }
  };

  // 포인트 체크 함수 (알림 발송 시 필요한 포인트 확인)
  const checkPointsForNotification = (type) => {
    // type: 'checkIn' or 'checkOut'
    const pushEnabled = notificationSettings.push?.enabled && notificationSettings.push?.[type];
    const alimtalkEnabled = notificationSettings.alimtalk?.enabled && notificationSettings.alimtalk?.[type];

    if (!pushEnabled && !alimtalkEnabled) {
      return true; // 알림 설정이 꺼져있으면 포인트 체크 불필요
    }

    // 필요 포인트 계산
    let requiredPoints = 0;
    if (pushEnabled) requiredPoints += 10;
    if (alimtalkEnabled) requiredPoints += 30;

    if (pointBalance < requiredPoints) {
      const result = window.confirm(
        `포인트가 부족합니다.\n\n` +
        `현재 포인트: ${pointBalance.toLocaleString()}P\n` +
        `필요 포인트: ${requiredPoints}P\n\n` +
        `충전하시겠습니까?\n\n` +
        `[확인] → 포인트 충전 페이지로 이동\n` +
        `[취소] → 알림 설정 페이지로 이동`
      );

      if (result) {
        navigate('/points');
      } else {
        navigate('/settings', { state: { activeTab: 'notification' } });
      }
      return false;
    }

    return true;
  };

  const handleCheckIn = async (studentId) => {
    // 포인트 체크
    if (!checkPointsForNotification('checkIn')) {
      return;
    }

    try {
      await api.post('/api/attendance/check-in', { studentId });
      // 알림이 발송되었으면 포인트 잔액 새로고침
      const pointsRes = await api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }));
      setPointBalance(pointsRes.data.balance || 0);
      loadAttendance();
    } catch (error) {
      alert(error.response?.data?.error || '등원 처리에 실패했습니다');
    }
  };

  const handleCheckOut = async (studentId) => {
    // 포인트 체크
    if (!checkPointsForNotification('checkOut')) {
      return;
    }

    try {
      await api.post('/api/attendance/check-out', { studentId });
      // 알림이 발송되었으면 포인트 잔액 새로고침
      const pointsRes = await api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }));
      setPointBalance(pointsRes.data.balance || 0);
      loadAttendance();
    } catch (error) {
      alert(error.response?.data?.error || '하원 처리에 실패했습니다');
    }
  };

  const getStatusBadge = (studentId) => {
    const record = attendance[studentId];
    if (!record) {
      return <span className="status-badge absent">미등원</span>;
    }
    if (record.checkOut?.time) {
      return <span className="status-badge completed">하원완료</span>;
    }
    return <span className="status-badge present">수업중</span>;
  };

  return (
    <div className="attendance-page">
      <div className="page-header">
        <h2>출결 관리</h2>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="filter-group">
          <label>반 선택</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">전체</option>
            {classes.map((cls) => {
              const scheduleStr = cls.schedule?.map(s => s.day).join(',') || '';
              const hasClass = cls.schedule?.some(s => s.day === selectedDay);
              return (
                <option key={cls._id} value={cls._id}>
                  {cls.name} ({scheduleStr || '일정없음'}) {hasClass ? '●' : ''}
                </option>
              );
            })}
          </select>
        </div>
        <div className="filter-group">
          <label>날짜 ({selectedDay}요일)</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </div>

      {/* 요일 불일치 경고 */}
      {selectedClass && !hasScheduleToday && (
        <div className="alert alert-warning">
          <FiAlertCircle /> 선택한 반은 {selectedDay}요일에 수업이 없습니다.
          {selectedClassData?.schedule?.length > 0 && (
            <span> (수업일: {selectedClassData.schedule.map(s => s.day).join(', ')})</span>
          )}
        </div>
      )}

      {/* 오늘이 아닌 날짜 경고 */}
      {!isToday && (
        <div className="alert alert-info">
          <FiAlertCircle /> {selectedDate}의 출결 기록을 조회 중입니다. 출석 처리는 오늘만 가능합니다.
        </div>
      )}

      {/* Attendance Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>학생</th>
              <th>상태</th>
              <th>입실 시간</th>
              <th>퇴실 시간</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="loading">로딩 중...</td></tr>
            ) : students.length === 0 ? (
              <tr><td colSpan="5" className="empty">학생이 없습니다</td></tr>
            ) : (
              students.map((student) => {
                const record = attendance[student._id];
                return (
                  <tr key={student._id}>
                    <td>
                      <div className="student-info">
                        <span className="name">{student.name}</span>
                        <span className="sub">{student.studentInfo?.school} {student.studentInfo?.grade}</span>
                      </div>
                    </td>
                    <td>{getStatusBadge(student._id)}</td>
                    <td>
                      {record?.checkIn?.time
                        ? format(new Date(record.checkIn.time), 'HH:mm')
                        : '-'}
                    </td>
                    <td>
                      {record?.checkOut?.time
                        ? format(new Date(record.checkOut.time), 'HH:mm')
                        : '-'}
                    </td>
                    <td>
                      {!isToday ? (
                        <span className="text-muted">-</span>
                      ) : !record?.checkIn?.time ? (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleCheckIn(student._id)}
                          disabled={selectedClass && !hasScheduleToday}
                          title={selectedClass && !hasScheduleToday ? '오늘 수업이 없는 반입니다' : ''}
                        >
                          <FiCheck /> 등원
                        </button>
                      ) : !record?.checkOut?.time ? (
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleCheckOut(student._id)}
                        >
                          <FiClock /> 하원
                        </button>
                      ) : (
                        <span className="done">완료</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="attendance-summary">
        <div className="summary-item">
          <span className="label">전체</span>
          <span className="value">{students.length}명</span>
        </div>
        <div className="summary-item present">
          <span className="label">등원</span>
          <span className="value">{Object.values(attendance).filter(a => a.checkIn?.time).length}명</span>
        </div>
        <div className="summary-item absent">
          <span className="label">미등원</span>
          <span className="value">{students.length - Object.values(attendance).filter(a => a.checkIn?.time).length}명</span>
        </div>
      </div>
    </div>
  );
}
